import { Injectable, inject, signal, effect, computed, DestroyRef } from '@angular/core';
import * as THREE from 'three';

import { ViewerCoreService } from './viewer-core.service.js';
import { onViewerEvents } from './event-dispatcher-bridge';

/**
 * Exposes input state (mouse position, selection, drag, pressed keys, hovered elements)
 * as Angular signals.
 *
 * The underlying InputHandler.js still owns the DOM listeners and event dispatch.
 * This service pulls state changes into Angular's reactivity system.
 */
@Injectable({ providedIn: 'root' })
export class InputHandlerService {
  private core = inject(ViewerCoreService);
  private destroyRef = inject(DestroyRef);

  readonly mouse = signal<{ x: number; y: number }>({ x: 0, y: 0 });
  readonly selection = signal<any[]>([]);
  readonly hoveredElements = signal<any[]>([]);
  readonly pressedKeys = signal<Record<number, boolean>>({});
  readonly isDragging = signal(false);
  readonly enabled = signal(true);

  readonly hasSelection = computed(() => this.selection().length > 0);
  readonly selectionCount = computed(() => this.selection().length);

  constructor() {
    effect(() => {
      const viewer = this.core.viewer();
      if (!viewer?.inputHandler) return;

      const inputHandler = viewer.inputHandler;

      // Initial sync
      this._syncFromHandler(inputHandler);

      // Listen for selection changes
      onViewerEvents(
        inputHandler,
        {
          selection_changed: (e: any) => {
            this.selection.set([...(e.selection ?? [])]);
          },
          delete: (e: any) => {
            // Selection was just deleted, will be empty after this
            this.selection.set([]);
          },
        },
        this.destroyRef,
      );
    });
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  deselectAll(): void {
    const viewer = this.core.viewer();
    viewer?.inputHandler?.deselectAll?.();
  }

  toggleSelection(object: any): void {
    const viewer = this.core.viewer();
    viewer?.inputHandler?.toggleSelection?.(object);
  }

  deselect(object: any): void {
    const viewer = this.core.viewer();
    viewer?.inputHandler?.deselect?.(object);
  }

  setEnabled(enabled: boolean): void {
    const viewer = this.core.viewer();
    if (viewer?.inputHandler) {
      viewer.inputHandler.enabled = enabled;
      this.enabled.set(enabled);
    }
  }

  addInputListener(listener: any): void {
    const viewer = this.core.viewer();
    viewer?.inputHandler?.addInputListener?.(listener);
  }

  removeInputListener(listener: any): void {
    const viewer = this.core.viewer();
    viewer?.inputHandler?.removeInputListener?.(listener);
  }

  registerInteractiveObject(object: any): void {
    const viewer = this.core.viewer();
    viewer?.inputHandler?.registerInteractiveObject?.(object);
  }

  removeInteractiveObject(object: any): void {
    const viewer = this.core.viewer();
    viewer?.inputHandler?.removeInteractiveObject?.(object);
  }

  getMousePointCloudIntersection(): any {
    const viewer = this.core.viewer();
    return viewer?.inputHandler?.getMousePointCloudIntersection?.() ?? null;
  }

  // ─── Private sync ─────────────────────────────────────────────────────────

  private _syncFromHandler(handler: any): void {
    if (handler.mouse) {
      this.mouse.set({ x: handler.mouse.x, y: handler.mouse.y });
    }
    this.selection.set([...(handler.selection ?? [])]);
    this.hoveredElements.set([...(handler.hoveredElements ?? [])]);
    this.pressedKeys.set({ ...(handler.pressedKeys ?? {}) });
    this.isDragging.set(handler.drag != null);
    this.enabled.set(handler.enabled ?? true);
  }
}
